import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import {
  PlannableUser,
  ResourceCategoryDef,
  ResourceSkill
} from '../../../Models/resource-assignment.model';
import { ResourcePlanningService } from '../../../Services/resource-planning.service';

interface UserRow {
  id: string;
  name: string;
  email: string;
  isPlannable: boolean;
  weeklyCapacityHours: number;
  skillIds: number[];
  calendarFeedUrl: string;
  saving: boolean;
}

@Component({
  selector: 'app-planning-admin-dialog',
  templateUrl: './planning-admin-dialog.component.html',
  styleUrls: ['./planning-admin-dialog.component.css']
})
export class PlanningAdminDialogComponent implements OnInit {
  loading = true;
  changed = false;

  allUsers: UserRow[] = [];
  skills: ResourceSkill[] = [];
  categories: ResourceCategoryDef[] = [];

  // Neuer Skill
  newSkillName = '';
  newSkillColor = '#607d8b';

  // Neue Kategorie
  newCatName = '';
  newCatColor = '#1976d2';
  newCatAbsence = false;

  // Benutzer hinzufügen
  userToAdd: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<PlanningAdminDialogComponent, boolean>,
    private service: ResourcePlanningService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.reload();
  }

  private reload(): void {
    this.loading = true;
    forkJoin({
      users: this.service.getAllUsers(),
      skills: this.service.getSkills(),
      categories: this.service.getCategories()
    }).subscribe({
      next: res => {
        this.skills = res.skills || [];
        this.categories = res.categories || [];
        this.allUsers = (res.users || []).map((u: PlannableUser) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          isPlannable: u.isPlannable,
          weeklyCapacityHours: u.weeklyCapacityHours,
          skillIds: (u.skills || []).map(s => s.id),
          calendarFeedUrl: u.calendarFeedUrl || '',
          saving: false
        }));
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Laden fehlgeschlagen.', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  // ---- Benutzer: nur planbare anzeigen ----
  get plannableUsers(): UserRow[] {
    return this.allUsers.filter(u => u.isPlannable);
  }

  get addableUsers(): UserRow[] {
    return this.allUsers.filter(u => !u.isPlannable);
  }

  addUser(): void {
    const row = this.allUsers.find(u => u.id === this.userToAdd);
    if (!row) {
      return;
    }
    row.isPlannable = true;
    this.persistUser(row, `${row.name} zur Planung hinzugefügt.`);
    this.userToAdd = null;
  }

  removeUser(row: UserRow): void {
    row.isPlannable = false;
    this.persistUser(row, `${row.name} aus der Planung entfernt.`);
  }

  saveUser(row: UserRow): void {
    this.persistUser(row, `${row.name} gespeichert.`);
  }

  private persistUser(row: UserRow, successMsg: string): void {
    row.saving = true;
    this.service.setUserProfile(row.id, {
      isPlannable: row.isPlannable,
      weeklyCapacityHours: row.weeklyCapacityHours,
      skillIds: row.skillIds,
      calendarFeedUrl: row.calendarFeedUrl.trim() || null
    }).subscribe({
      next: () => {
        row.saving = false;
        this.changed = true;
        this.snackBar.open(successMsg, 'OK', { duration: 2000 });
      },
      error: () => {
        row.saving = false;
        this.snackBar.open('Speichern fehlgeschlagen.', 'OK', { duration: 4000 });
      }
    });
  }

  // ---- Skills ----
  addSkill(): void {
    const name = this.newSkillName.trim();
    if (!name) {
      return;
    }
    this.service.createSkill({ name, color: this.newSkillColor }).subscribe({
      next: skill => {
        this.skills = [...this.skills, skill].sort((a, b) => a.name.localeCompare(b.name));
        this.newSkillName = '';
        this.newSkillColor = '#607d8b';
        this.changed = true;
      },
      error: () => this.snackBar.open('Skill anlegen fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  saveSkill(skill: ResourceSkill): void {
    this.service.updateSkill(skill.id, { name: skill.name, color: skill.color }).subscribe({
      next: () => { this.changed = true; this.snackBar.open('Skill gespeichert.', 'OK', { duration: 2000 }); },
      error: () => this.snackBar.open('Skill speichern fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  deleteSkill(skill: ResourceSkill): void {
    if (!confirm(`Skill "${skill.name}" wirklich löschen?`)) {
      return;
    }
    this.service.deleteSkill(skill.id).subscribe({
      next: () => {
        this.skills = this.skills.filter(s => s.id !== skill.id);
        this.allUsers.forEach(u => u.skillIds = u.skillIds.filter(id => id !== skill.id));
        this.changed = true;
      },
      error: () => this.snackBar.open('Skill löschen fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  // ---- Kategorien ----
  addCategory(): void {
    const name = this.newCatName.trim();
    if (!name) {
      return;
    }
    this.service.createCategory({ name, color: this.newCatColor, countsAsAbsence: this.newCatAbsence }).subscribe({
      next: cat => {
        this.categories = [...this.categories, cat].sort((a, b) => a.sortOrder - b.sortOrder);
        this.newCatName = '';
        this.newCatColor = '#1976d2';
        this.newCatAbsence = false;
        this.changed = true;
      },
      error: () => this.snackBar.open('Kategorie anlegen fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  saveCategory(cat: ResourceCategoryDef): void {
    this.service.updateCategory(cat.id, {
      name: cat.name,
      color: cat.color,
      countsAsAbsence: cat.countsAsAbsence
    }).subscribe({
      next: () => { this.changed = true; this.snackBar.open('Kategorie gespeichert.', 'OK', { duration: 2000 }); },
      error: () => this.snackBar.open('Kategorie speichern fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  deleteCategory(cat: ResourceCategoryDef): void {
    if (!confirm(`Kategorie "${cat.name}" wirklich löschen?`)) {
      return;
    }
    this.service.deleteCategory(cat.id).subscribe({
      next: () => {
        this.categories = this.categories.filter(c => c.id !== cat.id);
        this.changed = true;
      },
      error: () => this.snackBar.open('Kategorie löschen fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
